import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';

import { bannerFiltersAtom } from '../store/atoms/bannerFiltersAtom';
import type { BannerFilters } from '../types/bannerFilters';

/**
 * Hook para gerenciar os filtros (server-side) da listagem de Banner.
 *
 * @returns Filtros atuais e funções para atualizar e resetar.
 *
 * @example
 * ```tsx
 * const { filters, updateFilters, resetFilters } = useBannerFilters();
 * updateFilters({ page: 2 });
 * ```
 */
export function useBannerFilters() {
  const [filters, setFilters] = useAtom(bannerFiltersAtom);
  const resetFilters = useResetAtom(bannerFiltersAtom);

  /**
   * Atualiza parcialmente os filtros. Reseta para a primeira página, exceto
   * quando o próprio `page` é fornecido no `partial` (navegação de páginas).
   */
  const updateFilters = (partial: Partial<BannerFilters>) => {
    setFilters(prev => {
      const resetPage = 'page' in partial ? {} : { page: 1 };
      return { ...prev, ...resetPage, ...partial };
    });
  };

  return { filters, updateFilters, resetFilters } as const;
}
